import React from "react";
import { AgentEvaluationScenario } from "../../../services/api";
import { formatPercent } from "./shared";
import { UseAgentTestLab } from "./useAgentTestLab";

interface ScenarioListProps {
  lab: UseAgentTestLab;
}

const STATUS_DOT: Record<string, string> = {
  passed: "bg-green-500",
  failed: "bg-red-500",
  needs_review: "bg-amber-500",
  running: "bg-blue-500 animate-pulse",
  queued: "bg-blue-500 animate-pulse",
  draft: "bg-[var(--foreground-muted)]/40",
};

const dotClassFor = (scenario: AgentEvaluationScenario) => {
  const status = scenario.latest_result?.status ?? "draft";
  if (
    scenario.latest_result?.critical_failure ||
    scenario.latest_result?.verdict === "fail"
  ) {
    return STATUS_DOT.failed;
  }
  return STATUS_DOT[status] ?? STATUS_DOT.draft;
};

export const ScenarioList: React.FC<ScenarioListProps> = ({ lab }) => {
  const allSelected =
    lab.visibleScenarios.length > 0 &&
    lab.visibleScenarios.every((scenario) =>
      lab.selectedIds.includes(scenario.id)
    );
  const visibleSelectedCount = lab.visibleScenarios.filter((scenario) =>
    lab.selectedIds.includes(scenario.id)
  ).length;
  const hiddenSelectedCount = lab.selectedIds.length - visibleSelectedCount;

  return (
    <aside className="border-b xl:border-b-0 xl:border-r border-[var(--border)] bg-[var(--card-container)] flex flex-col">
      <div className="px-3 py-2.5 border-b border-[var(--border)] flex items-center gap-2">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={() =>
            lab.setSelectedIds(
              allSelected
                ? lab.selectedIds.filter(
                    (id) =>
                      !lab.visibleScenarios.some(
                        (scenario) => scenario.id === id
                      )
                  )
                : Array.from(
                    new Set([
                      ...lab.selectedIds,
                      ...lab.visibleScenarios.map((scenario) => scenario.id),
                    ])
                  )
            )
          }
          className="h-4 w-4 accent-[var(--accent)]"
          aria-label={allSelected ? "Deselect all visible" : "Select all visible"}
        />
        <span className="text-xs text-[var(--foreground-muted)] flex-1 min-w-0 truncate">
          {lab.visibleScenarios.length}{" "}
          {lab.visibleScenarios.length === 1 ? "scenario" : "scenarios"}
          {lab.selectedIds.length > 0 && (
            <>
              {" "}
              · {visibleSelectedCount} selected here
              {hiddenSelectedCount > 0 ? ` · ${hiddenSelectedCount} hidden` : ""}
            </>
          )}
        </span>
        <select
          value={lab.scenarioSort}
          onChange={(event) =>
            lab.setScenarioSort(
              event.target.value as typeof lab.scenarioSort
            )
          }
          className="text-xs bg-transparent border border-[var(--border)] rounded-md px-2 py-1"
        >
          <option value="recently-failed">Failed first</option>
          <option value="accuracy">By accuracy</option>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      <div className="flex-1 overflow-y-auto">
        {lab.visibleScenarios.length === 0 ? (
          <div className="p-6 text-sm text-[var(--foreground-muted)] text-center">
            No scenarios match this filter.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--border)]">
            {lab.visibleScenarios.map((scenario) => {
              const isActive = lab.selectedScenarioId === scenario.id;
              const score = scenario.latest_result?.score;
              return (
                <li key={scenario.id}>
                  <button
                    type="button"
                    onClick={() => {
                      lab.setSelectedScenarioId(scenario.id);
                      lab.setEditorSubTab("definition");
                    }}
                    className={`w-full text-left px-3 py-2.5 transition-colors flex items-center gap-2.5 ${
                      isActive
                        ? "bg-[var(--accent)]/10"
                        : "hover:bg-[var(--muted)]"
                    }`}
                    title={`Persona: ${scenario.persona_type}`}
                  >
                    <input
                      type="checkbox"
                      checked={lab.selectedIds.includes(scenario.id)}
                      onChange={(event) => {
                        event.stopPropagation();
                        lab.toggleScenarioSelection(scenario.id);
                      }}
                      onClick={(event) => event.stopPropagation()}
                      className="h-4 w-4 accent-[var(--accent)] flex-shrink-0"
                    />
                    <span
                      className={`h-2 w-2 rounded-full flex-shrink-0 ${dotClassFor(
                        scenario
                      )}`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium truncate">
                        {scenario.title || "Untitled scenario"}
                      </span>
                      <span className="block text-xs text-[var(--foreground-muted)] truncate">
                        {(scenario.languages ?? []).slice(0, 3).join(" · ") ||
                          "No language"}
                      </span>
                    </span>
                    <span className="text-xs text-[var(--foreground-muted)] tabular-nums flex-shrink-0">
                      {typeof score === "number" ? formatPercent(score) : "—"}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
};
